import { useState, useEffect, useCallback } from 'react'
import { ZONES, ELEMENTS, RARITY_CONFIG } from '../types'
import { LAUNCH_CREATURES } from '../data/creatures'
import { BountyAPI } from '../api/client'
import { useAuth } from '../store/auth'
import type { ZoneId, Element, BountyEntry, CreatureRarity, Creature } from '../types'
import EncounterModal from '../components/battle/EncounterModal'
import { useToast } from '../components/ui/Toast'
import { playSFX } from '../lib/audio'

type ActiveBounty = {
  id: string
  creatureName: string
  species: string
  element: Element
  rarity: string
  bountyValue: number
  requiredLevel: number
}

function enrichBounty(entry: BountyEntry): ActiveBounty | null {
  const creature = LAUNCH_CREATURES.find(c => c.id === entry.creatureId)
  if (!creature) return null
  return {
    id: entry.id,
    creatureName: creature.name,
    species: creature.species,
    element: creature.element,
    rarity: creature.rarity,
    bountyValue: entry.bountyValue,
    requiredLevel: entry.requiredCharacterLevel,
  }
}

function useCountdown(expiresAt: string) {
  const calc = useCallback(() => {
    const diff = Math.max(0, new Date(expiresAt).getTime() - Date.now())
    const h = Math.floor(diff / 3_600_000)
    const m = Math.floor((diff % 3_600_000) / 60_000)
    const s = Math.floor((diff % 60_000) / 1_000)
    return diff === 0 ? 'Expired' : h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`
  }, [expiresAt])

  const [label, setLabel] = useState(calc)
  useEffect(() => {
    const id = setInterval(() => setLabel(calc()), 1000)
    return () => clearInterval(id)
  }, [calc])
  return label
}

const ELEMENT_ICONS: Record<string, string> = {
  fire: '/assets/icons/element-fire.png',
  water: '/assets/icons/element-water.png',
  earth: '/assets/icons/element-earth.png',
  air: '/assets/icons/element-air.png',
  void: '/assets/icons/element-void.png',
  aether: '/assets/icons/element-aether.png',
}

function FilterPill({
  label, active, onClick,
}: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`font-bold text-xs tracking-wider px-3 py-1.5 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
        active
          ? 'text-[#FFE800] border-[#FFE800] border-opacity-60 bg-[#FFE800] bg-opacity-5'
          : 'border-[#3A3A5A] text-[#8A8AA5] hover:text-[#EAF6FF] hover:border-[#5A5A7A]'
      }`}
      style={{ fontFamily: 'Space Mono, monospace' }}
    >
      {label}
    </button>
  )
}

function BountySkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="rounded-xl border p-4 flex items-center gap-4 animate-pulse" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
          <div className="w-12 h-12 rounded-lg flex-shrink-0" style={{ background: 'rgba(58,58,90,0.3)' }} />
          <div className="flex-1 space-y-2">
            <div className="h-3 rounded w-2/5" style={{ background: 'rgba(58,58,90,0.3)' }} />
            <div className="h-2 rounded w-1/3" style={{ background: 'rgba(58,58,90,0.3)' }} />
            <div className="h-2 rounded w-1/2" style={{ background: 'rgba(58,58,90,0.3)' }} />
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <div className="h-3 rounded w-16" style={{ background: 'rgba(58,58,90,0.3)' }} />
            <div className="h-6 rounded w-20" style={{ background: 'rgba(58,58,90,0.3)' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

function BountyCard({
  entry,
  creature,
  playerLevel,
  onHunt,
  enrichBounty,
  index = 0,
}: {
  entry: BountyEntry
  creature: Creature
  playerLevel: number
  onHunt: (b: ActiveBounty) => void
  enrichBounty: (e: BountyEntry) => ActiveBounty | null
  index?: number
}) {
  const el = ELEMENTS[creature.element]
  const countdown = useCountdown(entry.expiresAt)
  const rarity = RARITY_CONFIG[creature.rarity as CreatureRarity]
  const zone = ZONES[entry.zoneId as ZoneId]
  const isExpired = countdown === 'Expired'
  const isUrgent = !isExpired && new Date(entry.expiresAt).getTime() - Date.now() < 3_600_000
  const levelLocked = playerLevel < entry.requiredCharacterLevel
  const canHunt = !isExpired && !levelLocked

  return (
    <div
      className={`group relative overflow-hidden rounded-xl border cursor-pointer transition-all duration-240 ${
        isExpired ? 'opacity-40' : ''
      }`}
      style={{
        background: 'linear-gradient(160deg, rgba(13,13,21,0.95), rgba(8,8,14,0.98))',
        borderColor: isExpired ? 'rgba(255,72,0,0.3)' : 'rgba(58,58,90,0.5)',
        boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
        animationDelay: `${index * 50}ms`,
      }}
      onMouseEnter={(e) => {
        if (!isExpired) {
          e.currentTarget.style.boxShadow = `0 0 16px ${el.color}40, 0 0 0 1px ${el.color}30`;
          e.currentTarget.style.borderColor = `${el.color}50`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.5)';
        e.currentTarget.style.borderColor = isExpired ? 'rgba(255,72,0,0.3)' : 'rgba(58,58,90,0.5)';
      }}
    >
      {/* Glow layer */}
      {!isExpired && (
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-240 pointer-events-none"
          style={{ background: `radial-gradient(circle at 50% 0%, ${el.color}08, transparent 70%)` }}
        />
      )}
      
      <div className="relative z-10 p-4 flex items-center gap-4">
        {/* Element icon with glow */}
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ 
            background: `${el.color}15`,
            border: `1px solid ${el.color}30`,
            boxShadow: `0 0 12px ${el.color}20`,
          }}
        >
          <img src={ELEMENT_ICONS[creature.element]} alt={el.label} className="w-8 h-8" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <h3 className="font-bold text-base text-[#EAF6FF] uppercase tracking-tight" style={{ fontFamily: 'Rajdhani, system-ui' }}>
              {creature.name}
            </h3>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full" 
              style={{ color: rarity.color, background: `${rarity.color}15`, fontFamily: 'Space Mono, monospace' }}>
              {rarity.label}
            </span>
          </div>
          <p className="text-xs text-[#8A8AA5] mb-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>{creature.species}</p>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border"
              style={{ 
                color: el.color, 
                borderColor: `${el.color}40`,
                background: `${el.color}10`,
                fontFamily: 'Space Mono, monospace',
              }}
            >
              {zone?.name ?? entry.zoneId}
            </span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${
              levelLocked
                ? 'border-red-500 border-opacity-50 text-red-400'
                : 'border-white border-opacity-10 text-[#8A8AA5]'
            }`}
              style={{ fontFamily: 'Space Mono, monospace' }}
            >
              Lv.{entry.requiredCharacterLevel}+
            </span>
          </div>
        </div>

        {/* Right side */}
        <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
          <p className="font-bold text-base" style={{ color: '#FFE800', fontFamily: 'Rajdhani, system-ui' }}>⚡ {entry.bountyValue}</p>
          <p className={`text-[10px] font-bold ${
            isExpired ? 'text-red-400' : isUrgent ? 'text-[#FF4800]' : 'text-[#5A5A7A]'
          }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {isExpired ? '✗ EXPIRED' : isUrgent ? `⚠ ${countdown}` : countdown}
          </p>
          <button
            disabled={!canHunt}
            onClick={() => { const b = enrichBounty(entry); if (b) { playSFX('modal-open'); onHunt(b) } }}
            title={levelLocked ? `Requires Level ${entry.requiredCharacterLevel}` : undefined}
            className={`px-4 py-1.5 mt-1 rounded-lg font-bold text-xs uppercase tracking-wider transition-all ${
              canHunt 
                ? 'bg-[#00F0FF] text-[#0A0A12] hover:bg-[#39FF14]' 
                : 'bg-[#3A3A5A] text-[#5A5A7A] cursor-not-allowed'
            }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {levelLocked ? `Lv.${entry.requiredCharacterLevel}` : 'HUNT'}
          </button>
        </div>
      </div>
    </div>
  )
}

const ZONE_TABS = [
  { label: 'All Zones', value: 'all' },
  ...Object.values(ZONES).map(z => ({ label: z.name, value: z.id })),
]

const RARITY_PILLS: { label: string; value: CreatureRarity | 'all'; color?: string }[] = [
  { label: 'All', value: 'all', color: '#8A8AA5' },
  { label: 'Drift',  value: 'drift', color: RARITY_CONFIG.drift.color },
  { label: 'Surge',  value: 'surge', color: RARITY_CONFIG.surge.color },
  { label: 'Rift',   value: 'rift', color: RARITY_CONFIG.rift.color },
  { label: 'Apex',   value: 'apex', color: RARITY_CONFIG.apex.color },
  { label: 'Mythic', value: 'mythic', color: RARITY_CONFIG.mythic.color },
]

export default function BountyBoard() {
  const { player, refreshPlayer } = useAuth()
  const { error: toastError, info } = useToast()
  const [activeBounty, setActiveBounty] = useState<ActiveBounty | null>(null)
  const [bounties, setBounties] = useState<BountyEntry[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [apiError, setApiError] = useState<string | null>(null)
  const [isOfflineCached, setIsOfflineCached] = useState(false)
  const [zoneFilter, setZoneFilter] = useState('all')
  const [rarityFilter, setRarityFilter] = useState<CreatureRarity | 'all'>('all')

  const load = useCallback(async () => {
    setLoading(true)
    setApiError(null)
    setIsOfflineCached(false)
    const res = await BountyAPI.getActiveBounties(
      zoneFilter !== 'all' ? zoneFilter : undefined
    )
    if (res.success && res.data) {
      setBounties(res.data)
      if (res.data.length > 0) playSFX('bounty-appear')
    } else {
      setApiError(res.error ?? 'Failed to load bounties')
      if (!navigator.onLine) {
        setIsOfflineCached(true)
        toastError('You\'re offline — showing cached bounties')
      } else {
        toastError('Could not reach bounty server')
      }
    }
    setLoading(false)
  }, [zoneFilter, toastError])

  useEffect(() => { load() }, [load])

  function handleVictory(_bountyId: string, coins: number) {
    setActiveBounty(null)
    info(`+${coins} ⚡ Atlas Coins added to your wallet`)
    refreshPlayer()
    load()
  }

  const playerLevel = player?.level ?? 0

  const displayBounties = (bounties ?? []).filter(entry => {
    if (rarityFilter === 'all') return true
    const creature = LAUNCH_CREATURES.find(c => c.id === entry.creatureId)
    return creature?.rarity === rarityFilter
  })

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Compact */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)' }}>
        <div className="flex items-center gap-3">
          <h1 className="font-bold text-lg uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>Bounty Board</h1>
          {isOfflineCached && (
            <span className="font-bold text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-wider"
                  style={{ borderColor: '#BF00FF60', color: '#BF00FF', background: '#BF00FF10', fontFamily: 'Space Mono, monospace' }}>
              📵 CACHED
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-95"
            style={{ background: 'rgba(13,13,21,0.8)', border: '1px solid rgba(58,58,90,0.5)' }}
          >
            {loading ? '⌛' : '↺'}
          </button>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full border"
               style={{ background: 'rgba(13,13,21,0.8)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <div className={`w-1.5 h-1.5 rounded-full ${apiError ? 'bg-[#FF4800]' : 'bg-[#00F0FF] animate-pulse'}`} />
            <span className="text-[10px] text-[#8A8AA5] uppercase" style={{ fontFamily: 'Space Mono, monospace' }}>{apiError ? 'Offline' : 'Live'}</span>
          </div>
        </div>
      </div>

      {/* Zone Filter - Horizontal scroll */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-3 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        {ZONE_TABS.map(tab => (
          <FilterPill
            key={tab.value}
            label={tab.label}
            active={zoneFilter === tab.value}
            onClick={() => setZoneFilter(tab.value)}
          />
        ))}
      </div>

      {/* Rarity Filter - Horizontal scroll */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-2">
        {RARITY_PILLS.map(pill => (
          <button
            key={pill.value}
            onClick={() => setRarityFilter(pill.value)}
            className={`font-bold text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
              rarityFilter === pill.value ? 'border-opacity-60' : 'border-[#3A3A5A] text-[#8A8AA5] hover:border-[#5A5A7A]'
            }`}
            style={{ 
              borderColor: rarityFilter === pill.value ? pill.color : undefined,
              color: rarityFilter === pill.value ? pill.color : undefined,
              fontFamily: 'Space Mono, monospace',
            }}
          >
            {pill.label}
          </button>
        ))}
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <BountySkeleton />
        ) : apiError && displayBounties.length === 0 ? (
          <div className="rounded-xl border p-8 text-center border-dashed" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <p className="text-3xl mb-3">📡</p>
            <p className="font-bold text-[#EAF6FF] mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>Board Unreachable</p>
            <p className="text-sm text-[#8A8AA5] mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>{apiError}</p>
            <button 
              onClick={load} 
              className="px-4 py-2 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80"
              style={{ background: '#FFE800', color: '#020510', fontFamily: 'Space Mono, monospace' }}
            >
              Try Again
            </button>
          </div>
        ) : displayBounties.length === 0 ? (
          <div className="rounded-xl border p-8 text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <p className="text-3xl mb-3">🎯</p>
            <p className="font-bold text-[#EAF6FF] mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>No Active Bounties</p>
            <p className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Check back later or explore a different zone.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {displayBounties.map((entry, index) => {
              const creature = LAUNCH_CREATURES.find(c => c.id === entry.creatureId)
              if (!creature) return null
              return (
                <BountyCard
                  key={entry.id}
                  entry={entry}
                  creature={creature}
                  playerLevel={playerLevel}
                  onHunt={setActiveBounty}
                  enrichBounty={enrichBounty}
                  index={index}
                />
              )
            })}
          </div>
        )}

        {/* Level hint for new players */}
        {playerLevel < 5 && (
          <div className="rounded-xl border p-3 mt-4 flex items-center gap-3"
               style={{ background: 'rgba(255,72,0,0.1)', borderColor: 'rgba(255,72,0,0.3)' }}>
            <span>⚠️</span>
            <p className="text-xs text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>
              Locked bounties require a higher crew level. Bond with creatures to level up.
            </p>
          </div>
        )}
      </div>

      {activeBounty && (
        <EncounterModal
          bounty={activeBounty}
          onClose={() => setActiveBounty(null)}
          onVictory={handleVictory}
        />
      )}
    </div>
  )
}
